/**
 * Fair Price Settings Modal
 *
 * Small dark-theme modal (opened from the gear next to the FP toggle in the
 * action bar) that edits ALL Fair Price strategy settings via the store's
 * updateSettings (GET/POST /api/fair-price-settings). Save applies an
 * optimistic local merge (updateSettings does that internally) and closes;
 * Cancel discards the local draft.
 *
 * Structure/styling mirror CopyTradingSettingsModal (portal + overlay +
 * header/content/footer, CSS variables for the dark theme).
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useFairPriceStore,
  FairPriceSettings,
} from '../store/fairPriceStore';
import './FairPriceSettingsModal.css';

interface FairPriceSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type NumberKey =
  | 'tpPoints'
  | 'slPoints'
  | 'bigCandlePts'
  | 'swingLookback'
  | 'migrationVolMult'
  | 'migrationMovePts'
  | 'migrationWindowBars'
  | 'migrationConsolBars'
  | 'migrationConsolRangePts';

type ToggleKey =
  | 'sessionsRth'
  | 'sessionsEvening'
  | 'sessionsPm'
  | 'athGuard'
  | 'migrationEnabled';

interface NumberField {
  key: NumberKey;
  label: string;
  step: number;
  min: number;
}

interface ToggleField {
  key: ToggleKey;
  label: string;
}

const RISK_FIELDS: NumberField[] = [
  { key: 'tpPoints', label: 'TP (points)', step: 0.25, min: 0 },
  { key: 'slPoints', label: 'SL (points)', step: 0.25, min: 0 },
  { key: 'bigCandlePts', label: 'Big candle (points)', step: 0.25, min: 0 },
  { key: 'swingLookback', label: 'Swing lookback (bars)', step: 1, min: 1 },
];

const SESSION_TOGGLES: ToggleField[] = [
  { key: 'sessionsRth', label: 'RTH session (9:29 box)' },
  { key: 'sessionsEvening', label: 'Evening sessions (6PM / 8PM)' },
  { key: 'sessionsPm', label: 'PM session (2PM)' },
  { key: 'athGuard', label: 'ATH guard (suppress shorts near all-time high)' },
];

const MIGRATION_FIELDS: NumberField[] = [
  { key: 'migrationVolMult', label: 'Volume multiple', step: 0.1, min: 0 },
  { key: 'migrationMovePts', label: 'Impulse move (points)', step: 0.25, min: 0 },
  { key: 'migrationWindowBars', label: 'Impulse window (bars)', step: 1, min: 1 },
  { key: 'migrationConsolBars', label: 'Consolidation (bars)', step: 1, min: 1 },
  { key: 'migrationConsolRangePts', label: 'Consolidation range (points)', step: 0.25, min: 0 },
];

export const FairPriceSettingsModal: React.FC<FairPriceSettingsModalProps> = ({
  isOpen,
  onClose,
}) => {
  // Store settings object is a stable reference between updates (no useShallow
  // needed for a direct property selector — repo zustand rule covers selectors
  // that BUILD new objects/arrays).
  const settings = useFairPriceStore((s) => s.settings);
  const [draft, setDraft] = useState<FairPriceSettings>(settings);
  const [isSaving, setIsSaving] = useState(false);

  // Refresh from the engine and re-snapshot the draft each time we open.
  useEffect(() => {
    if (!isOpen) return;
    useFairPriceStore.getState().fetchSettings();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setDraft(settings);
  }, [isOpen, settings]);

  if (!isOpen) return null;

  const setNumber = (key: NumberKey, raw: string): void => {
    const value = parseFloat(raw);
    setDraft((d) => ({ ...d, [key]: Number.isFinite(value) ? value : 0 }));
  };

  const setToggle = (key: ToggleKey, value: boolean): void => {
    setDraft((d) => ({ ...d, [key]: value }));
  };

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      // Optimistic: updateSettings merges locally first, then POSTs.
      await useFairPriceStore.getState().updateSettings(draft);
    } finally {
      setIsSaving(false);
    }
    onClose();
  };

  const renderNumberRow = (field: NumberField): React.ReactNode => (
    <label key={field.key} className="fp-settings-row">
      <span className="fp-settings-label">{field.label}</span>
      <input
        type="number"
        className="fp-settings-input"
        value={draft[field.key]}
        step={field.step}
        min={field.min}
        onChange={(e) => setNumber(field.key, e.target.value)}
      />
    </label>
  );

  const renderToggleRow = (field: ToggleField): React.ReactNode => (
    <label key={field.key} className="fp-settings-row fp-settings-toggle-row">
      <span className="fp-settings-label">{field.label}</span>
      <input
        type="checkbox"
        checked={draft[field.key]}
        onChange={(e) => setToggle(field.key, e.target.checked)}
      />
    </label>
  );

  // Portal to body level, escaping the chart's stacking context (same as the
  // other settings modals).
  return createPortal(
    <div className="fp-settings-overlay" onClick={onClose}>
      <div className="fp-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fp-settings-header">
          <h2>Fair Price Settings</h2>
          <button className="fp-settings-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="fp-settings-content">
          <div className="fp-settings-section">
            <h3>Risk / Structure</h3>
            {RISK_FIELDS.map(renderNumberRow)}
          </div>

          <div className="fp-settings-section">
            <h3>Sessions &amp; Guards</h3>
            {SESSION_TOGGLES.map(renderToggleRow)}
          </div>

          <div className="fp-settings-section">
            <h3>Anchor Migration</h3>
            {renderToggleRow({ key: 'migrationEnabled', label: 'Enable migration detection' })}
            <div className={draft.migrationEnabled ? '' : 'fp-settings-dimmed'}>
              {MIGRATION_FIELDS.map(renderNumberRow)}
            </div>
          </div>
        </div>

        <div className="fp-settings-footer">
          <button className="fp-settings-btn cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="fp-settings-btn save" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FairPriceSettingsModal;
